import APIService from "@/services/APIService"
import JWTService from "@/services/JWTService";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import SMStatus from "@/store/enums/SMStatus";
import SubscriberType from "@/store/enums/SubscriberType";

export interface User {
    token: string;
    account: string;
    subscriberType: SubscriberType,
    name: string;
    address: string;
    smStatus: SMStatus;
}

export interface UserAuthInfo {
    errors: unknown;
    user: User;
    isAuthenticated: boolean;
}

export interface SubscriberData {
    authState: number;
    smStatus: number;
}

export interface OperatorData {
    userName: string;
    userGroup: number;
    department: number;
    login: string;
    ipAddresses: string;
    description: string;
    recordStatus: number;
}

@Module
export default class AppModule extends VuexModule implements UserAuthInfo {
    errors = {};
    user = {} as User;
    isAuthenticated = !!JWTService.getToken();
    subscriberData = {} as SubscriberData;
    operatorData = {} as OperatorData;

    get currentUser(): User {
        return this.user;
    }

    get isUserAuthenticated(): boolean {
        return this.isAuthenticated;
    }

    get getErrors() {
        return this.errors;
    }

    get lastSubscriberData(): SubscriberData {
        return this.subscriberData;
    }

    get lastOperatorData(): OperatorData {
        return this.operatorData;
    }

    @Mutation
    [Mutations.SET_ERROR](error) {
        this.errors = { ...error };
    }

    @Mutation
    [Mutations.CLEAR_ERROR]() {
        this.errors = {};
    }

    @Mutation
    [Mutations.SET_AUTH](user) {
        this.isAuthenticated = true;
        this.user = user;
        this.errors = {};
        JWTService.saveToken(user.token);
    }

    @Mutation
    [Mutations.PURGE_AUTH]() {
        this.isAuthenticated = false;
        this.user = {} as User;
        this.errors = [];
        JWTService.destroyToken();
    }

    @Mutation
    [Mutations.SET_VALIDATED_USER](user) {
        this.isAuthenticated = true;
        this.user = user;
        this.errors = {};
    }

    @Mutation
    [Mutations.SET_SUBSCRIBER_DATA](subscriberData) {
        this.subscriberData = subscriberData;
    }

    @Mutation
    [Mutations.PURGE_SUBSCRIBER_DATA]() {
        this.subscriberData = {} as SubscriberData;
    }

    @Mutation
    [Mutations.SET_OPERATOR_DATA](operatorData) {
        this.operatorData = operatorData;
    }

    @Mutation
    [Mutations.PURGE_OPERATOR_DATA]() {
        this.operatorData = {} as OperatorData;
    }

    @Action
    [Actions.GET_REMOTEHOST]() {
        return APIService.post("/GetRemoteHost", "")
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;
            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.CHECK_USER_AUTH_STATE](credentials) {
        return APIService.post("/CheckUserAuthState", credentials)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;
            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.LOGIN](credentials) {
        return APIService.post("/UserAuthorize", credentials)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    this.context.commit(Mutations.SET_AUTH, {
                        token: data.Session_UID,
                        account: data.Account,
                        subscriberType: data.SubscriberType,
                        name: data.UserName,
                        address: data.Address,
                        smStatus: data.SMStatus
                    });
            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.LOGOUT]() {
        const  params = {
            Session_UID: JWTService.getToken()
        };

        const token = JWTService.getToken();
        if (token) {
            if (token.length != 0) {
                return APIService.post("/CloseUserSession", params)
                    .then(() => {
                        this.context.commit(Mutations.PURGE_AUTH);
                    })
                    .catch(({ response }) => {
                        this.context.commit(Mutations.PURGE_AUTH);

                        if (response.status != 200) {
                            this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                        } else
                            this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                    });
            }

        } else {
            this.context.commit(Mutations.PURGE_AUTH);
        }
    }

    @Action
    [Actions.VALIDATE_SESSION]() {
        const params = {
            Session_UID: JWTService.getToken()
        };

        return APIService.post("/UserSessionValidate", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    this.context.commit(Mutations.SET_VALIDATED_USER, {
                        token: JWTService.getToken(),
                        account: data.Account,
                        subscriberType: data.SubscriberType,
                        name: data.UserName,
                        address: data.Address,
                        smStatus: data.SMStatus
                    });
            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.USER_INFO]() {
        const params = {
            Session_UID: JWTService.getToken()
        };

        return APIService.post("/UserInfo", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data;
            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.USER_AUTH_STATE]() {
        const params = {
            Session_UID: JWTService.getToken()
        };

        return APIService.post("/UserAuthState", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.CHANGE_USER_PASSWORD](passwords) {
        const params = {
            Session_UID: JWTService.getToken(),
            NewPassword1: passwords.newPassword1,
            NewPassword2: passwords.newPassword2
        };

        return APIService.post("/ChangeUserPassword", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.GET_USER_DOCUMENT]() {
        const params = {
            Session_UID: JWTService.getToken()
        };

        return APIService.post("/GetUserDocument", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.GET_USER_REGISTER_POINTS]() {
        const params = {
            Session_UID: JWTService.getToken()
        };

        return APIService.post("/GetUserRegisterPointsList", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.REGISTER_NEW_READING](newReading) {
        const params = {
            Session_UID: JWTService.getToken(),
            R_AccountNumber: newReading.account,
            R_NewReading: newReading.value
        };

        return APIService.post("/RegisterNewReading", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.GET_REF_USER_GROUPS]() {
        const params = {
            Session_UID: JWTService.getToken()
        };

        return APIService.post("/GetRefUserGroups", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.GET_REF_RECORD_STASUSES]() {
        const params = {
            Session_UID: JWTService.getToken()
        };

        return APIService.post("/GetRefRecordStatuses", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.GET_REF_DEPARTMENTS]() {
        const params = {
            Session_UID: JWTService.getToken()
        };

        return APIService.post("/GetRefDepartments", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.GET_USERS](searchParams) {
        const params = {
            Session_UID: JWTService.getToken(),
            F_QuickSearch: searchParams.quickSearch,
            F_Department_UID: searchParams.searchDepartment
        };

        return APIService.post("/GetUsers", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.GET_USER](recordUID) {
        const params = {
            Session_UID: JWTService.getToken(),
            F_UID: recordUID
        };

        return APIService.post("/GetUser", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.UPDATE_USER](userData) {
        const params = {
            Session_UID: JWTService.getToken(),
            R_UID: userData.recordUID,
            R_RV: userData.recordVersion,
            R_UserName: userData.userName,
            R_UserGroup_UID: userData.userGroup,
            R_Department_UID: userData.department,
            R_WebAccess: userData.webAccess,
            R_Login: userData.login,
            R_Password: userData.password,
            R_IPAddresses: userData.ipAddresses,
            R_Description: userData.description,
            R_RS: userData.recordStatus
        };

        return APIService.post("/UpdateUser", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.GET_REF_SUBSCRIBERS](searchParams) {
        const params = {
            Session_UID: JWTService.getToken(),
            F_QuickSearch: searchParams.quickSearch,
            F_Department_UID: searchParams.searchDepartment
        };

        return APIService.post("/GetRefSubscribers", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.GET_REF_SUBSCRIBER](recordUID) {
        const params = {
            Session_UID: JWTService.getToken(),
            F_UID: recordUID
        };

        return APIService.post("/GetRefSubscriber", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.UPDATE_REF_SUBSCRIBER](subscriberData) {
        const params = {
            Session_UID: JWTService.getToken(),
            R_UID: subscriberData.recordUID,
            R_Password: subscriberData.password,
            R_AuthState: subscriberData.authState,
            R_SMStatus: subscriberData.smStatus
        };

        return APIService.post("/UpdateRefSubscriber", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }

    @Action
    [Actions.RESET_REF_SUBSCRIBER_LASTRRQS](subscriberUID) {
        const params = {
            Session_UID: JWTService.getToken(),
            R_Subscriber_UID: subscriberUID
        };

        return APIService.post("/ResetRefSubscriberLastRRQS", params)
            .then(({ data }) => {
                if (!("success" in data))
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
                else if (!data.success)
                    this.context.commit(Mutations.SET_ERROR, [data.response]);
                else
                    return data.response;

            })
            .catch(({ response }) => {
                if (response.status != 200) {
                    this.context.commit(Mutations.SET_ERROR, [`Непредвиденная ошибка! ${response.status + ", " + response.statusText}`]);
                } else
                    this.context.commit(Mutations.SET_ERROR, ["Непредвиденная ошибка!"]);
            });
    }
}